import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Formik } from 'formik';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { selectErrorReset, selectLoading, selectSuccess } from '../../../Selector/LoginSelector';
import { actions } from '../../../Slice/LoginSlice';
import { ReactComponent as CrossIcon } from '../assets/img/icons/cross.svg';

// application components goes here
import Input from './Input';
import Message from './Message';
import SmallButton from '../../common/components/SmallButton';

const LoginWrapper = styled.div`
  margin: 0px;
  padding: 25px 60px 15px 60px;
  border-radius: 30px;
  background-color: #fff;
  display: block;
  width: 100%;
  position: relative;
  @media all and (max-width: 1439px), all and (max-height: 749px) {
    padding: 0px 40px 20px 40px;
    border-radius: 21px;
    max-width: 400px;
  }
  .cross-btn {
    position: absolute;
    right: 18px;
    top: 20px;
    cursor: pointer;
    @media all and (max-width: 1439px), all and (max-height: 749px) {
      top: 12px;
      svg {
        width: 23px;
        height: 23px;
      }
    }
  }
  .content-wrapper {
    margin-top: 0px;
    margin-bottom: 0px;
    h2 {
      margin-top: 20px;
      font-family: "Roboto Slab";
    }
  }
  .input-field-spacing {
    margin-top: 30px;
    margin-bottom: 25px;
    /* margin-top: 50px;
    margin-bottom: 60px;
    @media all and (max-width: 1439px), all and (max-height: 749px) {
      margin-top: 30px;
      margin-bottom: 25px;
    } */
  }
  h2 {
    color: #424b50;
    font-size: 32px;
    margin: 35px 0 10px 0;
    font-weight: bold;
    text-align: left;
    @media all and (max-width: 1439px), all and (max-height: 749px) {
      font-size: 22px;
    }
  }

  p {
    font-size: 16px;
    color: #5e7079;
    margin: 15px 0;
    font-family: "Roboto";
    font-weight: 600;
    @media all and (max-width: 1439px), all and (max-height: 749px) {
      font-size: 12px;
      margin: 15px 0 0 0;
    }
    .span {
      cursor: pointer;
      margin: 0 5px;
      color: #1ec252 !important;
      font-weight: 700;
      text-decoration: none;
    }
  }
  input[type="text"],
  input[type="password"] {
    @media all and (max-width: 1439px), all and (max-height: 749px) {
      font-size: 16px;
      line-height: 20px;
      padding: 8px 18px;
      &.password + span {
        top: 13px;
      }
    }
  }
  label {
    @media all and (max-width: 1439px), all and (max-height: 749px) {
      margin: 16px 0 5px 0;
      font-size: 12px;
    }
  }
  button {
    @media all and (max-width: 1439px), all and (max-height: 749px) {
      font-size: 16px;
      padding: 9px 18px;
    }
  }
  .g-recaptcha > div {
    height: 85px !important;
    div > iframe {
      height: 85px !important;
    }
  }
`;
interface Props {
  setRedirectPath: (url: string) => void;
}

const initialState = {
  email: '',
};

const ForgetPasswordSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Required'),
});

const ForgotPassword: React.FC<Props> = function ForgotPassword(props: Props) {
  const { setRedirectPath } = props;
  const dispatch = useDispatch();
  const loading: boolean = useSelector(selectLoading);
  const success: boolean = useSelector(selectSuccess);
  const error: string = useSelector(selectErrorReset);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const getCapchaToken = useCallback(async () => {
    if (executeRecaptcha) {
      const capchaToken = await executeRecaptcha('ForgetPassword');
      return capchaToken;
    }
    return null;
  }, [executeRecaptcha]);
  useEffect(() => {
    if (success) {
      setRedirectPath('resetPassword');
    }
  }, [success]);

  const redirectPath = () => {
    dispatch(actions.resetForgetPassword(false));
    setRedirectPath('');
  };
  return (
    <LoginWrapper>
      <Formik
        initialValues={initialState}
        // validate={handleValidation}
        validationSchema={ForgetPasswordSchema}
        onSubmit={async (values) => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          const capchaToken = await getCapchaToken();
          const obj = {
            email: values.email,
            deviceToken: '',
            recaptcha: capchaToken,
            appName: '',
          };
          dispatch(actions.forgetPassword(obj));
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          handleBlur,
        }) => (
          <>
            <span
              tabIndex={0}
              role="button"
              onKeyPress={redirectPath}
              className="cross-btn"
              onClick={redirectPath}
              aria-label="Close"
            >
              <CrossIcon />
            </span>
            <div className="content-wrapper">
              <h2>Forgot Password</h2>
              {error ? (
                <Message type="error" label="Error!" message={error} />
              ) : null}
              <p>
                Please enter the email address you use to log into sales intelligence.
                If we find a match, we’ll send a password reset link to that email.
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="input-field-spacing">
                <Input
                  inputName="email"
                  inputLabel="Sales Intelligence account email"
                  inputType="text"
                  inputPlaceholder="Enter your email address..."
                  inputValue={values.email}
                  onInputChange={handleChange}
                  onInputBlur={handleBlur}
                  error={touched.email && errors.email ? errors.email : ''}
                />
              </div>
              <SmallButton
                buttonType="submit"
                title={loading ? 'Loading ...' : 'Reset Password Now'}
                disabled={!_.isEmpty(errors) || loading}
              />
            </form>
            <p>
              Just forgot your
              <span
                onClick={() => setRedirectPath('username')}
                className="span me-0 mr-0"
                tabIndex={0}
                role="button"
                onKeyPress={() => setRedirectPath('username')}
                aria-label="Username"
              >
                username
              </span>
              ?
            </p>
          </>
        )}
      </Formik>
    </LoginWrapper>
  );
};

export default ForgotPassword;
